import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, Save, Eraser, KeyRound, Calculator } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IntegrationsSection } from "@/components/IntegrationsSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [days, setDays] = useState(30);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "operator" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [s, u] = await Promise.all([api.get("/settings"), api.get("/users")]);
    setSettings(s.data);
    setDays(s.data.retention_days);
    setUsers(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRetention = async () => {
    try { const { data } = await api.put("/settings", { retention_days: Number(days) }); setSettings(data); toast.success(`Labels older than ${data.retention_days} days will be deleted nightly`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const cleanupNow = async () => {
    if (!window.confirm(`Delete all labels, batches and print jobs older than ${settings?.retention_days} days now?`)) return;
    setBusy(true);
    try { const { data } = await api.post("/settings/cleanup-now"); toast.success(`Deleted ${data.deleted_labels} label(s) and ${data.deleted_batches} batch(es)`); load(); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const reparseQty = async () => {
    setBusy(true);
    try { const { data } = await api.post("/settings/reparse-qty"); toast.success(`Recalculated · ${data.fixed} qty, ${data.marketplace_fixed} marketplace, ${data.tracking_filled} tracking, ${data.duplicates_flagged} duplicate fix(es)`); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const addUser = async (e) => {
    e.preventDefault();
    try { await api.post("/users", form); toast.success("Account created"); setForm({ name: "", email: "", password: "", role: "operator" }); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const setRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success(`${u.name} is now ${role}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPassword = async (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 6 chars)`);
    if (!pw) return;
    try { await api.patch(`/users/${u.id}`, { password: pw }); toast.success("Password updated"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeUser = async (u) => {
    if (!window.confirm(`Delete account ${u.name} (${u.emp_id || u.email})?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Account deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Admin only</div>
        <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Settings</h1>
      </div>

      <section className="rounded-md border border-slate-800 bg-[#111827] p-5 sm:p-6" data-testid="retention-section">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">Data retention</h2>
        <p className="mt-1 text-sm text-slate-400">Shipping labels, their order/tracking IDs, batches and print history older than this many days are deleted automatically every night at 2:30 AM IST.</p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Keep labels for (days)</Label>
            <Input data-testid="retention-days-input" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(e.target.value)} className="w-40 font-mono border-slate-700 bg-slate-950" />
          </div>
          <Button data-testid="retention-save-button" className="bg-blue-600 hover:bg-blue-700" onClick={saveRetention}><Save className="mr-2 h-4 w-4" /> Save</Button>
          <Button data-testid="cleanup-now-button" variant="outline" className="border-red-700 text-red-300 hover:bg-red-950/40" disabled={busy} onClick={cleanupNow}><Eraser className="mr-2 h-4 w-4" /> Run cleanup now</Button>
          <Button data-testid="reparse-qty-button" variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" disabled={busy} onClick={reparseQty} title="Re-read label quantities and marketplace with the latest parser"><Calculator className="mr-2 h-4 w-4" /> Recalculate labels</Button>
        </div>
        <div className="mt-3 font-mono text-xs text-slate-500" data-testid="last-cleanup-info">
          {settings?.last_cleanup ? `Last cleanup ${new Date(settings.last_cleanup.ran_at).toLocaleString()} · ${settings.last_cleanup.deleted_labels} labels, ${settings.last_cleanup.deleted_batches} batches removed` : "No cleanup has run yet."}
        </div>
      </section>

      <section className="space-y-4" data-testid="users-section">
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight sm:text-xl">Accounts</h2>
        <form onSubmit={addUser} className="grid gap-3 rounded-md border border-slate-800 bg-[#111827] p-5 sm:grid-cols-5" data-testid="add-user-form">
          <Input data-testid="user-name-input" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-700 bg-slate-950" />
          <Input data-testid="user-email-input" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-700 bg-slate-950" />
          <Input data-testid="user-password-input" type="password" placeholder="Password (min 6)" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="border-slate-700 bg-slate-950" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="user-role-select" className="border-slate-700 bg-slate-950"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-700 bg-slate-900"><SelectItem value="operator">Operator</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
          </Select>
          <Button type="submit" data-testid="add-user-button" className="bg-blue-600 hover:bg-blue-700"><UserPlus className="mr-2 h-4 w-4" /> Add account</Button>
        </form>
        <p className="text-xs text-slate-500">Operators can upload, match, print and manage picklists. Only admins can delete batches, mappings and labels, manage accounts or change settings.</p>
        <div className="overflow-x-auto rounded-md border border-slate-800 bg-[#111827]">
          <Table data-testid="users-table">
            <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Name</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Login</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Designation / Dept</TableHead>
              <TableHead className="font-mono text-[11px] uppercase tracking-widest">Role</TableHead>
              <TableHead className="w-24" />
            </TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-slate-800/70" data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm">{u.name}{u.id === user?.id && <span className="ml-2 text-xs text-slate-500">(you)</span>}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-300">
                    {u.auth_source === "emp"
                      ? <span className="flex items-center gap-2"><span className="rounded-sm border border-emerald-800 bg-emerald-950/40 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300">Emp</span>{u.emp_id}</span>
                      : u.email}
                  </TableCell>
                  <TableCell className="text-xs text-slate-400" data-testid={`user-designation-${u.id}`}>{u.auth_source === "emp" ? [u.designation, u.department].filter(Boolean).join(" · ") || "—" : "Email account"}</TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => setRole(u, v)} disabled={u.id === user?.id}>
                      <SelectTrigger data-testid={`user-role-${u.id}`} className="h-8 w-32 border-slate-700 bg-slate-950 font-mono text-xs uppercase"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-slate-700 bg-slate-900"><SelectItem value="operator">Operator</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" title="Reset password" disabled={u.auth_source === "emp"} onClick={() => resetPassword(u)} data-testid={`user-reset-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
                      {u.id !== user?.id && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-300" onClick={() => removeUser(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <IntegrationsSection />
    </div>
  );
}
